const MINUTE = 60 * 1000;

function hashString(value) {
  let hash = 2166136261;

  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return hash >>> 0;
}

function utcDateKey(date) {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function activeSale(date, hours, durationMinutes) {
  const dateKey = utcDateKey(date);
  const latestStartMinute = 60 - durationMinutes;

  for (const hour of hours) {
    const startMinute = hashString(`${dateKey}:${hour}`) % (latestStartMinute + 1);
    const start = Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      hour,
      startMinute
    );
    const end = start + durationMinutes * MINUTE;

    if (date.getTime() >= start && date.getTime() < end) {
      return { start, end, key: `${dateKey}:${hour}:${startMinute}` };
    }
  }

  return null;
}

class FlashSaleController extends HTMLElement {
  connectedCallback() {
    this.standardAnnouncements = this.querySelector('[data-standard-announcements]');
    this.flashSale = this.querySelector('[data-flash-sale]');
    this.code = this.dataset.code?.trim();
    this.enabled = this.dataset.enabled === 'true' && Boolean(this.code);
    this.durationMinutes = Number.parseInt(this.dataset.durationMinutes, 10) || 15;
    this.hours = (this.dataset.windowHours || '8,14,18')
      .split(',')
      .map((hour) => Number.parseInt(hour, 10))
      .filter((hour) => Number.isInteger(hour) && hour >= 0 && hour <= 23);
    this.sale = null;
    this.checkoutInProgress = false;

    this.onCheckoutClick = this.onCheckoutClick.bind(this);
    this.onCheckoutSubmit = this.onCheckoutSubmit.bind(this);

    document.addEventListener('click', this.onCheckoutClick, true);
    document.addEventListener('submit', this.onCheckoutSubmit, true);

    this.update();
    this.timer = window.setInterval(() => this.update(), 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this.timer);
    document.removeEventListener('click', this.onCheckoutClick, true);
    document.removeEventListener('submit', this.onCheckoutSubmit, true);
    document.documentElement.classList.remove('flash-sale-active');
    delete document.documentElement.dataset.flashSaleEnd;
  }

  update() {
    const now = new Date();
    this.sale = this.enabled ? activeSale(now, this.hours, this.durationMinutes) : null;
    const isActive = Boolean(this.sale);

    if (this.standardAnnouncements) this.standardAnnouncements.hidden = isActive;
    if (this.flashSale) this.flashSale.hidden = !isActive;
    this.classList.toggle('flash-sale-live', isActive);

    document.querySelectorAll('[data-flash-surface]').forEach((surface) => {
      surface.hidden = !isActive;
    });
    this.syncCheckoutDiscountFields(isActive);

    document.documentElement.classList.toggle('flash-sale-active', isActive);

    if (!isActive) {
      delete document.documentElement.dataset.flashSaleEnd;
      return;
    }

    document.documentElement.dataset.flashSaleEnd = String(this.sale.end);

    const remainingSeconds = Math.max(0, Math.ceil((this.sale.end - now.getTime()) / 1000));
    const minutes = Math.floor(remainingSeconds / 60);
    const seconds = remainingSeconds % 60;

    document.querySelectorAll('[data-flash-countdown]').forEach((countdown) => {
      countdown.textContent = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    });
  }

  discountCheckoutUrl() {
    return `/discount/${encodeURIComponent(this.code)}?redirect=${encodeURIComponent('/checkout')}`;
  }

  cartUpdateUrl() {
    const root = window.Shopify?.routes?.root || '/';
    return `${root}cart/update.js`;
  }

  syncCheckoutDiscountFields(isActive) {
    document.querySelectorAll('form[action*="/cart"], form#cart-form').forEach((form) => {
      let input = form.querySelector('[data-flash-discount-input]');

      if (!isActive) {
        input?.remove();
        return;
      }

      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'discount';
        input.dataset.flashDiscountInput = '';
        form.append(input);
      }

      input.value = this.code;
    });
  }

  async checkoutWithDiscount() {
    if (this.checkoutInProgress || !this.sale) return;

    this.checkoutInProgress = true;

    try {
      const response = await fetch(this.cartUpdateUrl(), {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ discount: this.code }),
      });

      if (!response.ok) throw new Error(`Discount update failed with status ${response.status}`);

      await response.json();
      window.location.assign('/checkout');
    } catch (error) {
      console.error('Flash sale discount could not be attached to the cart.', error);
      window.location.assign(this.discountCheckoutUrl());
    }
  }

  onCheckoutClick(event) {
    if (!this.sale || !(event.target instanceof Element)) return;

    const checkoutControl = event.target.closest(
      'a[href*="/checkout"], button[name="checkout"], input[name="checkout"], shopify-buy-it-now-button'
    );

    if (!checkoutControl) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.checkoutWithDiscount();
  }

  onCheckoutSubmit(event) {
    if (!this.sale || !(event.target instanceof HTMLFormElement)) return;

    const submitter = event.submitter;
    const isCheckoutSubmit =
      submitter?.getAttribute('name') === 'checkout' || event.target.action.includes('/checkout');

    if (!isCheckoutSubmit) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.checkoutWithDiscount();
  }
}

if (!customElements.get('flash-sale-controller')) {
  customElements.define('flash-sale-controller', FlashSaleController);
}
